import React, { useState, useEffect } from 'react';
import { Modal, Spinner } from '../../components/ui';
import { errMsg } from '../../utils/format';
import api, { downloadFile } from '../../utils/api';
import { useFetch } from '../../hooks/useFetch';

export default function EditVendorModal({ vendor, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:             vendor.name         || '',
    contactName:      vendor.contact_name || '',
    email:            vendor.email        || '',
    phone:            vendor.phone        || '',
    address:          vendor.address      || '',
    notes:            vendor.notes        || '',
    generateFeedCsv:  vendor.generate_feed_csv  || false,
    feedSchedule:     vendor.feed_schedule      || 'daily',
    feedDayOfWeek:    vendor.feed_day_of_week   ?? 1,
    feedDiscountPct:  vendor.feed_discount_pct  ?? '',
    dropshipFeePct:   vendor.dropship_fee_pct   ?? '',
    feedEmailRecipients: vendor.feed_email_recipients || '',
    vendorType:       vendor.vendor_type        || 'supplier',
    creditTerms:      vendor.credit_terms       || 'net_30',
    contactEmail:     vendor.contact_email      || '',
  });
  const [saving, setSaving]         = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [newKey, setNewKey]         = useState(null);
  const [revoking, setRevoking]     = useState(null);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const { data: apiKeys, loading: keysLoading, refetch: refetchKeys } = useFetch(
    `/api/vendor-feed/keys/${vendor.id}`
  );

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.patch(`/api/vendors/${vendor.id}`, form);
      onSaved('Vendor updated successfully'); onClose();
    } catch(err) { alert(errMsg(err)); }
    finally { setSaving(false); }
  };

  const generateKey = async () => {
    const name = window.prompt('Enter a name for this API key (e.g. "Production")');
    if (!name) return;
    setGeneratingKey(true);
    try {
      const { data } = await api.post('/api/vendor-feed/keys', { vendorId: vendor.id, name });
      setNewKey(data);
      refetchKeys();
    } catch(err) { alert(errMsg(err)); }
    finally { setGeneratingKey(false); }
  };

  const revokeKey = async (keyId) => {
    if (!window.confirm('Revoke this API key? This cannot be undone.')) return;
    setRevoking(keyId);
    try {
      await api.delete(`/api/vendor-feed/keys/${keyId}`);
      refetchKeys();
    } catch(err) { alert(errMsg(err)); }
    finally { setRevoking(null); }
  };

  const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

  return (
    <Modal title={`Edit — ${vendor.name}`} onClose={onClose} wide
      footer={<>
        {form.vendorType === 'b2b_customer' && (
          <button type="button" className="btn btn-sm" onClick={() => {
            const filename = `${vendor.name.replace(/\s+/g,'_')}_feed_${new Date().toISOString().slice(0,10)}.csv`;
            downloadFile(`/api/vendor-feed/vendor/${vendor.id}/inventory.csv`, filename);
          }}>
            <i className="ti ti-file-download" /> Download feed CSV
          </button>
        )}
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" form="edit-vendor-form" type="submit">{saving?'Saving…':'Save changes'}</button>
      </>}>
      <form id="edit-vendor-form" onSubmit={save}>

        {/* Basic info */}
        <div className="form-group"><label>Vendor name *</label><input required value={form.name} onChange={e=>set('name',e.target.value)} /></div>
        <div className="form-row">
          <div className="form-group">
            <label>Vendor type</label>
            <select value={form.vendorType} onChange={e=>set('vendorType',e.target.value)}>
              <option value="supplier">Supplier</option>
              <option value="b2b_customer">B2B Customer</option>
            </select>
          </div>
          {form.vendorType === 'b2b_customer' && (
            <div className="form-group">
              <label>Credit terms</label>
              <select value={form.creditTerms} onChange={e=>set('creditTerms',e.target.value)}>
                <option value="due_on_receipt">Due on Receipt</option>
                <option value="net_30">Net 30</option>
                <option value="net_60">Net 60</option>
                <option value="net_90">Net 90</option>
              </select>
            </div>
          )}
        </div>
        {form.vendorType === 'b2b_customer' && (
          <div className="form-group">
            <label>Invoice email address</label>
            <input type="email" value={form.contactEmail} onChange={e=>set('contactEmail',e.target.value)}
              placeholder="billing@customer.com" />
            <small style={{ fontSize:11, color:'var(--text-tertiary)' }}>
              Invoice is sent to this address when tracking is added to their order.
            </small>
          </div>
        )}
        <div className="form-row">
          <div className="form-group"><label>Contact name</label><input value={form.contactName} onChange={e=>set('contactName',e.target.value)} /></div>
          <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e=>set('email',e.target.value)} /></div>
        </div>
        <div className="form-group"><label>Phone</label><input value={form.phone} onChange={e=>set('phone',e.target.value)} /></div>
        <div className="form-group"><label>Address</label><textarea value={form.address} onChange={e=>set('address',e.target.value)} /></div>
        <div className="form-group"><label>Notes</label><textarea value={form.notes} onChange={e=>set('notes',e.target.value)} /></div>

        {/* Vendor feed CSV schedule */}
        <div style={{ borderTop:'1px solid var(--border)', marginTop:16, paddingTop:16 }}>
          <div style={{ fontWeight:600, fontSize:13, marginBottom:10 }}>Vendor Feed CSV Export</div>
          <div className="form-group" style={{ display:'flex', alignItems:'center', gap:10 }}>
            <input type="checkbox" id="generate-feed" style={{ width:'auto' }}
              checked={form.generateFeedCsv}
              onChange={e=>set('generateFeedCsv',e.target.checked)} />
            <label htmlFor="generate-feed" style={{ marginBottom:0, cursor:'pointer' }}>
              Generate feed CSV automatically
            </label>
          </div>
          <div className="form-row" style={{ marginTop:8 }}>
            <div className="form-group">
              <label>Vendor discount %</label>
              <input type="number" min="0" max="100" step="0.01"
                value={form.feedDiscountPct}
                onChange={e=>set('feedDiscountPct', e.target.value)}
                placeholder="e.g. 30 for 30% off" />
              <small style={{ fontSize:11, color:'var(--text-tertiary)' }}>
                Vendor price = list price × (1 − discount%). Leave blank to omit vendor price from feed.
              </small>
            </div>
            <div className="form-group">
              <label>Drop ship fee %</label>
              <input type="number" min="0" max="100" step="0.01"
                value={form.dropshipFeePct}
                onChange={e=>set('dropshipFeePct', e.target.value)}
                placeholder="e.g. 3 for 3% drop ship fee" />
              <small style={{ fontSize:11, color:'var(--text-tertiary)' }}>
                Added as a separate line item on B2B invoices. Calculated as % of order subtotal.
              </small>
            </div>
          </div>
          {form.generateFeedCsv && (
            <>
            <div className="form-row" style={{ marginTop:8 }}>
              <div className="form-group">
                <label>Schedule</label>
                <select value={form.feedSchedule} onChange={e=>set('feedSchedule',e.target.value)}>
                  <option value="daily">Daily (every day at 6:00 AM)</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              {form.feedSchedule === 'weekly' && (
                <div className="form-group">
                  <label>Day of week</label>
                  <select value={form.feedDayOfWeek} onChange={e=>set('feedDayOfWeek',parseInt(e.target.value))}>
                    {DAYS.map((d,i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Email recipients (semicolon-separated)</label>
              <input type="text" value={form.feedEmailRecipients}
                onChange={e=>set('feedEmailRecipients',e.target.value)}
                placeholder="vendor@example.com; buyer@example.com" />
              <small style={{ fontSize:11, color:'var(--text-tertiary)' }}>
                CSV will be emailed as an attachment on the configured schedule. Leave blank to skip email.
              </small>
            </div>
            </>
          )}
          {vendor.feed_last_generated_at && (
            <p style={{ fontSize:12, color:'var(--text-tertiary)', margin:'4px 0 0' }}>
              Last generated: {new Date(vendor.feed_last_generated_at).toLocaleString()}
              {vendor.feed_last_file && <span> — <code style={{ fontSize:11 }}>{vendor.feed_last_file.split('/').pop()}</code></span>}
            </p>
          )}
        </div>

        {/* API Keys */}
        <div style={{ borderTop:'1px solid var(--border)', marginTop:16, paddingTop:16 }}>
          <div style={{ display:'flex', alignItems:'center', marginBottom:10 }}>
            <div style={{ fontWeight:600, fontSize:13 }}>Vendor Feed API Keys</div>
            <div style={{ flex:1 }} />
            <button type="button" className="btn btn-sm btn-primary" onClick={generateKey} disabled={generatingKey}>
              <i className="ti ti-key" /> {generatingKey ? 'Generating…' : 'Generate key'}
            </button>
          </div>

          {newKey && (
            <div style={{ background:'#f0fdf4', border:'1px solid #86efac', borderRadius:8, padding:12, marginBottom:12 }}>
              <div style={{ fontWeight:600, fontSize:13, color:'#166534', marginBottom:4 }}>
                <i className="ti ti-circle-check" style={{ marginRight:6 }} />New API key generated — save it now, it will not be shown again
              </div>
              <code style={{ fontSize:12, background:'#dcfce7', padding:'4px 8px', borderRadius:4, display:'block', wordBreak:'break-all' }}>
                {newKey.key}
              </code>
              <div style={{ fontSize:11, color:'#166534', marginTop:6 }}>
                Usage: <code>Authorization: Bearer {newKey.key}</code>
              </div>
              <div style={{ marginTop:8, fontSize:12, color:'#166534' }}>
                <strong>Endpoints:</strong><br/>
                JSON: <code>GET https://inventory.summitfishingequipment.com/api/vendor-feed/inventory</code><br/>
                CSV: <code>GET https://inventory.summitfishingequipment.com/api/vendor-feed/inventory.csv</code>
              </div>
              <button type="button" className="btn btn-sm" style={{ marginTop:8 }} onClick={() => setNewKey(null)}>Dismiss</button>
            </div>
          )}

          {keysLoading ? <Spinner /> : (
            <table style={{ width:'100%', fontSize:12 }}>
              <thead>
                <tr style={{ borderBottom:'1px solid var(--border)' }}>
                  <th style={{ textAlign:'left', padding:'4px 8px' }}>Name</th>
                  <th style={{ textAlign:'left', padding:'4px 8px' }}>Prefix</th>
                  <th style={{ textAlign:'left', padding:'4px 8px' }}>Created</th>
                  <th style={{ textAlign:'left', padding:'4px 8px' }}>Last used</th>
                  <th style={{ textAlign:'left', padding:'4px 8px' }}>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(apiKeys || []).length === 0 ? (
                  <tr><td colSpan={6} style={{ padding:12, textAlign:'center', color:'var(--text-tertiary)' }}>No API keys — generate one above</td></tr>
                ) : (apiKeys || []).map(k => (
                  <tr key={k.id} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ padding:'6px 8px', fontWeight:500 }}>{k.name}</td>
                    <td style={{ padding:'6px 8px' }}><code>{k.key_prefix}…</code></td>
                    <td style={{ padding:'6px 8px', color:'var(--text-tertiary)' }}>{new Date(k.created_at).toLocaleDateString()}</td>
                    <td style={{ padding:'6px 8px', color:'var(--text-tertiary)' }}>{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : 'Never'}</td>
                    <td style={{ padding:'6px 8px' }}>
                      <span style={{ fontSize:11, padding:'2px 8px', borderRadius:4, background: k.active ? '#dcfce7' : '#fee2e2', color: k.active ? '#166534' : '#dc2626', fontWeight:600 }}>
                        {k.active ? 'Active' : 'Revoked'}
                      </span>
                    </td>
                    <td style={{ padding:'6px 8px' }}>
                      {k.active && (
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => revokeKey(k.id)} disabled={revoking === k.id}>
                          {revoking === k.id ? '…' : 'Revoke'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </form>
    </Modal>
  );
}
